import {
    UpdateTaskSubTicketAssigneeInput,
    UpdateTaskSubTicketInput,
} from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/inputs';
import { TaskSubTicketRef } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/refs';
import {
    DesignerPickerItemResource,
    TaskSubTicketResource,
} from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';

import { RefReconcileOutcome, SubTicketPartialReport, ViewRefreshOutcome } from '../../store/reconcile';
import { SubTicketFormValues, buildUpdateSubTicketPayload } from '../../utils';

export interface SubmitUpdateCapabilities {
    canUpdate: boolean;
    canManageAssignee: boolean;
}

export interface SubmitUpdateDeps {
    updateSubTicket: (id: ID, input: UpdateTaskSubTicketInput) => Promise<TaskSubTicketRef>;
    updateSubTicketAssignee: (id: ID, input: UpdateTaskSubTicketAssigneeInput) => Promise<TaskSubTicketRef>;
    // Takes every committed ref, because a save can commit two endpoints and each returns its
    // own flags. Collapsing them would drop whichever ref lost.
    reconcileRefs: (refs: (TaskSubTicketRef | null)[], taskId: ID) => Promise<RefReconcileOutcome>;
    refreshViews: () => Promise<ViewRefreshOutcome>;
    resolveConflict: (error: any, action: 'reassign') => { message: string } | null;
}

export interface SubmitUpdateParams {
    subTicket: TaskSubTicketResource;
    capabilities: SubmitUpdateCapabilities;
    values: SubTicketFormValues;
    selectedDesigner: DesignerPickerItemResource | null;
    deps: SubmitUpdateDeps;
}

export interface SubmitUpdateResult {
    ok: boolean;
    didUpdate: boolean;
    didReassign: boolean;
    conflictMessage: string | null;
    partial: SubTicketPartialReport | null;
    blockedReason: 'no-capability' | 'designer-required' | null;

    // The real read-back outcomes, handed to the caller whole rather than flattened into
    // booleans. Messaging has to be composed from what actually happened on each surface, and a
    // derived flag is one more place for the two to drift apart.
    //
    // Both are null when nothing was written, so "not attempted" stays distinct from "failed".
    views: ViewRefreshOutcome | null;
    reconciled: RefReconcileOutcome | null;

    // Merged facts, so the caller can report them regardless of which ref carried them
    participationAutoAdded: boolean;
    dueAfterParentDeadline: boolean;
}

const emptyResult = (blockedReason: SubmitUpdateResult['blockedReason']): SubmitUpdateResult => ({
    ok: false,
    didUpdate: false,
    didReassign: false,
    conflictMessage: null,
    partial: null,
    blockedReason,
    views: null,
    reconciled: null,
    participationAutoAdded: false,
    dueAfterParentDeadline: false,
});

// The whole submission, extracted from the component so both endpoints and every capability
// combination are exercisable without mounting anything.
//
// UPDATE and ASSIGNEE_MANAGE are genuinely independent permissions, so each endpoint is gated
// on its own capability and nothing else. In particular the reassignment decision never looks
// at `selectedDesigner` first: a picker left populated by a previous modal, or pre-seeded from
// a stale global store, must not be able to smuggle a reassignment into an UPDATE-only save.
export const submitSubTicketUpdate = async ({
    subTicket,
    capabilities,
    values,
    selectedDesigner,
    deps,
}: SubmitUpdateParams): Promise<SubmitUpdateResult> => {
    const { canUpdate, canManageAssignee } = capabilities;

    if (!canUpdate && !canManageAssignee) {
        return emptyResult('no-capability');
    }

    // Whenever the assignee permission is held the Designer field is on screen, and a designer
    // from THIS Request must be selected before anything is sent — whether or not the caller can
    // also edit the fields.
    //
    // The caller resolves `selectedDesigner` against the current Request's picker, so an empty,
    // failed, still-loading or foreign picker arrives here as null. Saving the fields anyway
    // would silently leave the assignment unmanaged while the modal showed a Designer field that
    // did nothing, and a stale selection would send another Request's designer to the wire.
    if (canManageAssignee && (selectedDesigner == null || selectedDesigner.user == null)) {
        return emptyResult('designer-required');
    }

    // Capability first, identity second, and every dereference null-safe
    const isReassigning =
        canManageAssignee &&
        selectedDesigner != null &&
        selectedDesigner.user != null &&
        selectedDesigner.user.id !== subTicket.assignee_user.id;

    let assigneeRef: TaskSubTicketRef | null = null;

    // Reassignment goes first because it is the only part that can be refused: a running timer
    // blocks it with a 409. Attempting it first means a refusal leaves the phase untouched
    // rather than half-saved.
    if (isReassigning && selectedDesigner) {
        try {
            assigneeRef = await deps.updateSubTicketAssignee(subTicket.id, {
                assignee_user_id: selectedDesigner.user.id,
            });
        } catch (e) {
            const conflict = deps.resolveConflict(e, 'reassign');
            if (conflict) {
                return { ...emptyResult(null), conflictMessage: conflict.message };
            }
            throw e;
        }
    }

    let updateRef: TaskSubTicketRef | null = null;
    let partial: SubmitUpdateResult['partial'] = null;

    if (canUpdate) {
        try {
            updateRef = await deps.updateSubTicket(subTicket.id, buildUpdateSubTicketPayload(values));
        } catch (e) {
            // The reassignment already committed and only the field update failed
            if (assigneeRef) {
                partial = {
                    committed: 'The designer reassignment',
                    failed: 'the scope, deliverables and due date',
                };
            } else {
                throw e;
            }
        }
    }

    // BOTH refs, never `assigneeRef ?? updateRef`. Either endpoint can independently report
    // that it auto-added a participant or that the phase now falls past the parent deadline,
    // and discarding the losing ref would silently lose whichever fact it held.
    const reconciled = await deps.reconcileRefs([assigneeRef, updateRef], subTicket.task_id);

    const views = await deps.refreshViews();

    return {
        ok: partial === null,
        didUpdate: updateRef !== null,
        didReassign: assigneeRef !== null,
        conflictMessage: null,
        partial,
        blockedReason: null,
        views,
        reconciled,
        participationAutoAdded: reconciled.participationAutoAdded,
        dueAfterParentDeadline: reconciled.dueAfterParentDeadline,
    };
};
